import { useState, useEffect, useCallback } from "react";
import { getMachineHistory, getLatestLog } from "../api/client";
import SensorChart from "../components/SensorChart";
import AlarmTable from "../components/AlarmTable";
import {
  ArrowLeft,
  Activity,
  Thermometer,
  Zap,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import LiveClock from "../components/LiveClock";

const statusConfig = {
  critical: {
    label: "KRİTİK",
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/50",
    icon: <AlertTriangle className="w-5 h-5 text-red-400" />,
    ring: "ring-red-500",
  },
  warning: {
    label: "UYARI",
    color: "text-yellow-400",
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/50",
    icon: <AlertCircle className="w-5 h-5 text-yellow-400" />,
    ring: "ring-yellow-500",
  },
  normal: {
    label: "NORMAL",
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/50",
    icon: <CheckCircle className="w-5 h-5 text-green-400" />,
    ring: "ring-green-500",
  },
};

const BOYA_SENSORS = [
  {
    key: "furnace_zone1_temp",
    label: "Fırın Zone 1",
    unit: "°C",
    threshold: 235,
  },
  {
    key: "furnace_zone2_temp",
    label: "Fırın Zone 2",
    unit: "°C",
    threshold: 255,
  },
  {
    key: "furnace_zone3_temp",
    label: "Fırın Zone 3",
    unit: "°C",
    threshold: 245,
  },
  {
    key: "band_tension",
    label: "Bant Gerilimi",
    unit: "N/mm²",
    threshold: 14,
    invert: true,
  },
  {
    key: "pump_pressure",
    label: "Pompa Basıncı",
    unit: "bar",
    threshold: 2.5,
    invert: true,
  },
  { key: "vibration_rms", label: "Titreşim RMS", unit: "mm/s", threshold: 4.5 },
  { key: "bearing_temp", label: "Rulman Sıcaklığı", unit: "°C", threshold: 70 },
];

const ALKALI_SENSORS = [
  {
    key: "bath_conductivity",
    label: "Banyo İletkenliği",
    unit: "mS/cm",
    threshold: 110,
  },
  { key: "bath_temp", label: "Banyo Sıcaklığı", unit: "°C", threshold: 82 },
  { key: "ph", label: "pH", unit: "", threshold: 10.5, invert: true },
  { key: "brush_current", label: "Fırça Akımı", unit: "A", threshold: 17 },
  { key: "vibration_rms", label: "Titreşim RMS", unit: "mm/s", threshold: 4.0 },
  {
    key: "pump_pressure",
    label: "Fırça Basıncı",
    unit: "bar",
    threshold: 2.5,
    invert: true,
  },
];

const TANDEM_SENSORS = [
  {
    key: "rolling_force",
    label: "Hadde Kuvveti",
    unit: "kN",
    threshold: 10000,
  },
  { key: "roll_temp", label: "Merdane Sıcaklığı", unit: "°C", threshold: 85 },
  { key: "motor_torque", label: "Motor Torku", unit: "kNm", threshold: 160 },
  {
    key: "emulsion_pressure",
    label: "Emülsiyon Basıncı",
    unit: "bar",
    threshold: 9,
    invert: true,
  },
  { key: "motor_current", label: "Motor Akımı", unit: "A", threshold: 360 },
  { key: "vibration_rms", label: "Titreşim RMS", unit: "mm/s", threshold: 5.0 },
];

function getSensors(hatType) {
  if (hatType === "BOYA") return BOYA_SENSORS;
  if (hatType === "ALKALI") return ALKALI_SENSORS;
  if (hatType === "TANDEM") return TANDEM_SENSORS;
  return [];
}

function SensorGauge({ sensor, value }) {
  if (value === null || value === undefined || value === -999) return null;

  const val = parseFloat(value);
  const threshold = sensor.threshold;
  const invert = sensor.invert;

  let risk;
  if (invert) {
    risk = val < threshold ? "high" : "normal";
  } else {
    risk = val > threshold ? "high" : "normal";
  }

  const color = risk === "high" ? "text-red-400" : "text-green-400";
  const bgBar = risk === "high" ? "bg-red-500" : "bg-green-500";

  const maxVal = threshold * 1.3;
  const pct = Math.min((val / maxVal) * 100, 100);

  return (
    <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700/50">
      <div className="flex justify-between items-start mb-2">
        <span className="text-xs text-slate-400">{sensor.label}</span>
        {risk === "high" && (
          <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
        )}
      </div>
      <div className={`text-2xl font-bold ${color} mb-1`}>
        {val.toFixed(1)}
        <span className="text-sm font-normal text-slate-400 ml-1">
          {sensor.unit}
        </span>
      </div>
      <div className="w-full bg-slate-700 rounded-full h-1.5 mt-2">
        <div
          className={`h-1.5 rounded-full transition-all duration-700 ${bgBar}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-slate-500 mt-1">
        Eşik: {threshold} {sensor.unit}
      </div>
    </div>
  );
}

export default function MachineDetail({ machine, onBack }) {
  const [history, setHistory] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = useCallback(async () => {
    try {
      const [histRes, latestRes] = await Promise.all([
        getMachineHistory(machine.id),
        getLatestLog(machine.id),
      ]);
      setHistory(histRes.data);
      setLatest(latestRes.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [machine.id]);

  useEffect(() => {
    fetchData();
  }, [machine.id]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const status = latest?.ai_status || machine.status || "normal";
  const cfg = statusConfig[status] || statusConfig.normal;
  const prob = latest?.ai_probability
    ? Math.round(parseFloat(latest.ai_probability) * 100)
    : 0;
  const sensors = getSensors(machine.hat_type);

  const alarmHistory = history
    .filter((h) => parseFloat(h.ai_probability) >= 0.7)
    .map((h) => ({
      name: machine.name,
      ai_probability: h.ai_probability,
      ai_status: h.ai_status,
      timestamp: h.timestamp,
    }));

  const tabs = [
    { id: "overview", label: "Genel Bakış" },
    { id: "charts", label: "Sensör Grafikleri" },
    { id: "alarms", label: `Alarm Geçmişi (${alarmHistory.length})` },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Dashboard</span>
            </button>
            <div className="w-px h-6 bg-slate-700" />
            <div>
              <h1 className="text-lg font-bold text-white">{machine.name}</h1>
              <p className="text-xs text-slate-400">{machine.hat_type} Hattı</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-green-400 animate-pulse" : "bg-slate-500"}`}
              />
              <span className="text-xs text-slate-400">
                {/* {lastUpdate ? lastUpdate.toLocaleTimeString("tr-TR") : ""} */}
                <LiveClock />
              </span>
            </div>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                autoRefresh
                  ? "border-green-500/50 text-green-400 hover:bg-green-500/10"
                  : "border-slate-600 text-slate-400 hover:bg-slate-800"
              }`}
            >
              {autoRefresh ? "Canlı ●" : "Başlat"}
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:bg-slate-800"
            >
              <RefreshCw className="w-3 h-3" />
              Yenile
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1 pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Risk Skoru Banner */}
            <div
              className={`rounded-2xl border p-6 mb-8 ${cfg.bg} ${cfg.border}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div
                    className={`w-16 h-16 rounded-full ring-4 ${cfg.ring} flex items-center justify-center ${cfg.bg}`}
                  >
                    <span className={`text-xl font-black ${cfg.color}`}>
                      %{prob}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {cfg.icon}
                      <span className={`text-xl font-bold ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      Anlık Arıza İhtimali — AI Modeli (XGBoost)
                    </p>
                    <p className="text-slate-500 text-xs mt-1">
                      Son ölçüm:{" "}
                      {latest?.timestamp
                        ? new Date(latest.timestamp).toLocaleString("tr-TR")
                        : "-"}
                    </p>
                  </div>
                </div>

                {/* Risk Bar */}
                <div className="flex-1 min-w-64">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>Normal</span>
                    <span>Uyarı</span>
                    <span>Kritik</span>
                  </div>
                  <div className="relative w-full h-5 rounded-full overflow-hidden">
                    {/* Arka plan */}
                    <div className="absolute inset-0 flex">
                      <div
                        className="h-full bg-green-600/40"
                        style={{ width: "40%" }}
                      />
                      <div
                        className="h-full bg-yellow-600/40"
                        style={{ width: "30%" }}
                      />
                      <div
                        className="h-full bg-red-600/40"
                        style={{ width: "30%" }}
                      />
                    </div>
                    {/* Dolgu — prob kadar dolar */}
                    <div
                      className={`absolute top-0 left-0 h-full transition-all duration-700 opacity-80 ${
                        prob >= 70
                          ? "bg-red-500"
                          : prob >= 40
                            ? "bg-yellow-500"
                            : "bg-green-500"
                      }`}
                      style={{ width: `${prob}%` }}
                    />
                    {/* Parlak kenarlık efekti */}
                    <div
                      className={`absolute top-0 h-full w-1 transition-all duration-700 ${
                        prob >= 70
                          ? "bg-red-200"
                          : prob >= 40
                            ? "bg-yellow-200"
                            : "bg-green-200"
                      }`}
                      style={{ left: `calc(${Math.max(prob, 1)}% - 2px)` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>%0</span>
                    <span>%40</span>
                    <span>%70</span>
                    <span>%100</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab İçerikleri */}
            {activeTab === "overview" && (
              <div>
                <h3 className="text-slate-300 font-semibold mb-4 flex items-center gap-2">
                  <Thermometer className="w-4 h-4 text-blue-400" />
                  Anlık Sensör Değerleri
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {sensors.map((s) => (
                    <SensorGauge
                      key={s.key}
                      sensor={s}
                      value={latest?.[s.key]}
                    />
                  ))}
                </div>
              </div>
            )}

            {activeTab === "charts" && (
              <div>
                <h3 className="text-slate-300 font-semibold mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  Son 24 Saat — Sensör Trendleri
                </h3>
                <SensorChart data={history} hatType={machine.hat_type} />
              </div>
            )}

            {activeTab === "alarms" && (
              <div>
                <h3 className="text-slate-300 font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  Yüksek Risk Alarm Geçmişi
                </h3>
                <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                  <AlarmTable events={alarmHistory} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
